import React, {
	ChangeEventHandler,
	MouseEventHandler,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import classNames from 'classnames';

import DesktopLayout from '../../../layouts/DesktopLayout';
import MobileLayout from '../../../layouts/MobileLayout';
import useDeviceType from '../../../providers/DeviceType/hooks';
import i18n from '../../../providers/Locale';
import ComponentType from '../../../types/Component';
import Header from '../../Header';
import Button from '../Button';
import ArrowDownIcon from '../icons/ArrowDown';

import styles from './style.module.scss';

export type CustomSelectOption<T = string> = {
	label: string;
	value: T;
	labelJSX?: JSX.Element;
	labelInHead?: string | JSX.Element;
	disabled?: boolean;
	hidden?: boolean;
};

export type CustomSelectProps = {
	options: CustomSelectOption[];
	onChange: (option: string, name: string) => void;
	name: string;
	value?: string;
	error?: string;
	label?: string | JSX.Element;
	placeholder?: string;
	searchable?: boolean;
	subLabel?: string;
	position?: 'top' | 'bottom' | 'right' | 'left';
};

const CustomSelect: ComponentType<CustomSelectProps> = (props) => {
	const {
		name,
		options,
		label,
		onChange,
		value,
		error,
		placeholder,
		searchable,
		position,
		subLabel,
	} = props;
	const { isMobile } = useDeviceType();
	const [search, setSearch] = useState<string>('');
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const selectedOption = useMemo(() => {
		return options.find((option) => option.value === value);
	}, [value, options]);

	const isEmptyList = useMemo(
		() => options.length <= 1 && selectedOption,
		[options, selectedOption]
	);

	const onWindowClick = useCallback(() => {
		setIsOpen(false);
	}, [isOpen]);

	const onWindowEsc = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		},
		[isOpen]
	);

	const onInputClick: MouseEventHandler<HTMLDivElement> = () => {
		setTimeout(() => setIsOpen(!isOpen), 50);
	};

	const onOptionClick = (event: React.MouseEvent, option: CustomSelectOption) => {
		if (option.disabled) return event.stopPropagation();
		onChange(option.value, name);
	};

	const onChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
		const value = event.currentTarget.value;

		setSearch(value);
	};

	const searchableFilter = (option: CustomSelectOption): boolean => {
		const lowerSearch = search.toLowerCase();

		if (!searchable) return true;
		if (option.label?.toLowerCase().includes(lowerSearch)) return true;
		if (option.value?.toLowerCase().includes(lowerSearch)) return true;

		return false;
	};

	const onGoBack = useCallback(() => {}, []);

	useEffect(() => {
		setSearch('');

		if (isOpen) {
			if (isMobile) document.body.style.overflow = 'hidden';

			window?.addEventListener('click', onWindowClick);
			window?.addEventListener('keydown', onWindowEsc);
		} else {
			if (isMobile) document.body.style.overflow = '';

			window?.removeEventListener('click', onWindowClick);
			window?.removeEventListener('keydown', onWindowEsc);
		}

		return () => {
			window?.removeEventListener('click', onWindowClick);
			window?.removeEventListener('keydown', onWindowEsc);
		};
	}, [isOpen]);

	const mainClassNames = useMemo(() => {
		return classNames(styles.select, 'custom-select', {
			[styles.selectOpened]: isOpen,
			[styles.selectError]: error,
			[styles.selectEmpty]: isEmptyList,
		});
	}, [isOpen, error, isEmptyList]);

	return (
		<div className={mainClassNames}>
			{typeof label === 'string' && <div className={styles.label}>{label}</div>}
			{typeof label === 'object' && label}

			<div className={classNames(['custom-select__input', styles.input])} onClick={onInputClick}>
				<Button align={'fill'} className={styles.head} fullWidth withShadow>
					<div className={styles.labelWrapper}>
						{selectedOption?.labelInHead ||
							selectedOption?.labelJSX ||
							selectedOption?.label ||
							placeholder}
						{subLabel && <div className={styles.subLabel}>{subLabel}</div>}
					</div>
					<div className={styles.headArrow}>
						<ArrowDownIcon size={24} />
					</div>
				</Button>
				{error && <span className={styles.error}>{error}</span>}
				<DesktopLayout>
					<div
						className={classNames([
							'custom-select__options',
							styles.options,
							position === 'top' && styles.optionsTop,
							position === 'right' && styles.positionRight,
						])}>
						{searchable && (
							<div className={styles.searchable} onClick={(e) => e.stopPropagation()}>
								<input
									autoFocus
									type="text"
									value={search}
									onChange={onChangeSearch}
									placeholder={i18n('search')}
								/>
							</div>
						)}
						{options.filter(searchableFilter).map((option, i) => {
							const isSelected = option.value === value;

							return (
								<div
									className={classNames(styles.optionsItem, {
										[styles.optionsItemSelected]: isSelected,
										[styles.optionsItemDisabled]: option.disabled,
									})}
									key={`${option.label} + ${option.value} + ${i}`}
									onClick={(event) => onOptionClick(event, option)}>
									{option.labelJSX || option.label}
								</div>
							);
						})}
					</div>
				</DesktopLayout>
				<MobileLayout>
					<div className={styles.options}>
						<div className={styles.optionsHeader}>
							<Header title={label} collapsed onGoBack={onGoBack} />
						</div>
						<div className={styles.optionsBody}>
							{options.filter(searchableFilter).map((option, i) => {
								const isSelected = option.value === value;

								return (
									<div
										className={classNames(styles.optionsItem, {
											[styles.optionsItemSelected]: isSelected,
											[styles.optionsItemDisabled]: option.disabled,
										})}
										key={`${option.label} + ${option.value} + ${i}`}
										onClick={(event) => onOptionClick(event, option)}>
										{option.labelJSX || option.label}
									</div>
								);
							})}
						</div>
					</div>
				</MobileLayout>
			</div>
		</div>
	);
};

export default CustomSelect;
